<template>
    <tenancies-get
        :page="page"
        :archived="archived"
        :include="include"
        :property="$route.query.property"
        v-slot="{
            data: tenancies,
            meta,
            error,
            is_loading,
            has_loaded,
            get
        }"
    >
        <div v-if="!has_loaded || error">
            <base-loading
                :error="error"
                :retry="get"
                class="py-6"
            />

            <error-modal
                v-if="error"
                :error="error"
            />
        </div>

        <div v-else>
            <base-hint
                v-if="$gate.role('landlord')"
                name="landlords-tenancies-index"
            >
                <template slot="heading">
                    Your tenancies
                </template>

                <p>
                    You can add and manage as many tenancies as you like with Rentora. They'll all be listed here - past and present.
                </p>
            </base-hint>

            <base-hint
                v-else-if="$gate.role('tenant')"
                name="tenants-tenancies-index"
            >
                <template slot="heading">
                    Your tenancies
                </template>

                <p class="mb-3">
                    Your landlord/agent will send you an invitation to join a tenancy on Rentora.
                </p>

                <p>
                    Tenancies you’ve joined will be listed here - past and present.
                </p>
            </base-hint>

            <div class="mb-6 bg-white rounded shadow">
                <header class="flex items-center px-4 py-3 sm:px-6 md:py-3">
                    <div class="flex-grow">
                        <h1 class="text-lg font-normal">Tenancies</h1>
                    </div>
                    <div class="w-6 h-6 mr-3">
                        <base-svg
                            v-show="is_loading"
                            name="icon-loading"
                            class="w-full h-full fill-current text-grey"
                        />
                    </div>
                    <base-dropdown placement="bottom-end">
                        <button
                            slot="trigger"
                            slot-scope="{ is_open, toggle }"
                            :class="{ 'is-active': is_open }"
                            class="btn btn-with-icon"
                            type="button"
                            @click.prevent="toggle"
                        >
                            <div class="flex items-center w-full h-full">
                                <base-svg
                                    name="icon-funnel"
                                    class="w-6 h-6 md:mr-2"
                                />
                                <span class="hidden md:inline">Filter</span>
                            </div>
                        </button>
                        <div
                            slot="menu"
                            class="w-64 p-3 text-white rounded shadow-lg bg-grey-darker mt-3px"
                        >
                            <label class="flex items-center leading-none cursor-pointer">
                                <input-checkbox
                                    v-model="archived"
                                    class="mr-3"
                                />
                                <span>Archived</span>
                            </label>
                        </div>
                    </base-dropdown>
                    <router-link
                        v-if="$gate.role('landlord')"
                        :to="{ name: $router.fix('dashboard.*.tenancies.create') }"
                        class="ml-3 btn btn-blue btn-with-icon"
                    >
                        <div class="flex items-center w-full h-full">
                            <base-svg
                                name="icon-add-circle"
                                class="w-6 h-6 md:mr-2"
                            />

                            <span class="hidden md:inline">New Tenancy</span>
                        </div>
                    </router-link>
                </header>

                <template v-if="meta.count > 0">
                    <tenancies-list :tenancies="tenancies">
                        <truncate-hack
                            slot="primary"
                            slot-scope="{ tenancy }"
                        >
                            {{ $gate.role('landlord') ? tenancy.property.name : tenancy.property.address.name }}
                        </truncate-hack>

                        <truncate-hack
                            slot="secondary"
                            slot-scope="{ tenancy }"
                        >
                            <template v-if="$gate.role('landlord')">
                                <template v-if="tenancy.residencies && tenancy.residencies.meta.count > 0">
                                    {{ tenancy.residencies.data | comma-delimited({
                                        callback: (residency) => residency.occupant.name,
                                        total: tenancy.residencies.meta.total
                                    }) }}
                                </template>
                                <em
                                    v-else
                                    class="italic text-grey-darker"
                                >
                                    No tenants
                                </em>
                            </template>
                            <template v-else>
                                {{ tenancy.property.landlord.name }}
                            </template>
                        </truncate-hack>
                    </tenancies-list>

                    <footer class="px-4 text-sm font-semibold rounded-b bg-grey-bg sm:px-6 text-grey-darker">
                        <base-pagination
                            v-model="page"
                            :per-page="meta.per_page"
                            :from="meta.from"
                            :to="meta.to"
                            :last-page="meta.last_page"
                            :count="meta.count"
                            :total="meta.total"
                            :is-disabled="is_loading"
                        />
                    </footer>
                </template>

                <div
                    v-else
                    class="relative py-16 border-t"
                >
                    <div class="max-w-sm px-6 mx-auto text-center">
                        <h2 class="mb-2 text-xl font-semibold">
                            <template v-if="$gate.role('landlord') && meta.gross === 0">
                                Add your first tenancy
                            </template>
                            <template v-else>
                                No tenancies found
                            </template>
                        </h2>

                        <div class="text-base text-grey-darker">
                            <template v-if="$gate.role('landlord') && meta.gross === 0">
                                <p>You haven't added a tenancy yet.</p>
                                <p class="md:hidden">
                                    Tap <!--
                                    --><base-svg
                                        name="icon-add-circle"
                                        class="inline w-4 h-4 -mt-1"
                                    /><!--
                                    --> above to get started.
                                </p>
                                <p class="hidden md:block">
                                    Click "<!--
                                    --><base-svg
                                        name="icon-add-circle"
                                        class="inline w-4 h-4 -mt-1"
                                    /><!--
                                    --> New Tenancy" above to get started.
                                </p>
                            </template>

                            <template v-else-if="$gate.role('tenant') && meta.gross === 0">
                                <p class="mb-6">You haven't joined a tenancy yet.</p>

                                <a
                                    :href="$laroute.route('invitations.create')"
                                    class="mx-auto btn btn-with-icon btn-lg"
                                    target="_blank"
                                    rel="noopener"
                                >
                                    <div class="flex items-center w-full h-full">
                                        <base-svg
                                            name="icon-external-window"
                                            class="w-6 h-6 mr-2 fill-primary-grey fill-secondary-grey-dark"
                                        />

                                        Invite your landlord
                                    </div>
                                </a>
                            </template>

                            <template v-else>
                                <p>There's nothing here!</p>

                                <router-link
                                    :to="{
                                        name: $router.fix('dashboard.*.tenancies.index')
                                    }"
                                    class="text-blue"
                                >
                                    Reset filters &amp; go to page 1
                                </router-link>
                            </template>
                        </div>
                    </div>

                    <base-svg
                        v-if="$gate.role('landlord') && meta.gross === 0"
                        name="misc-arrow-up"
                        class="absolute top-0 right-0 w-4 mt-3 mr-8 sm:mr-10 md:mr-24 md:w-5 md:mt-4 fill-primary-blue-light"
                    />
                </div>
            </div>

            <welcome-modal
                v-if="$gate.role('landlord')"
            />
        </div>
    </tenancies-get>
</template>

<script>
    import PaginationMixin from '../../../../mixins/pagination'
    import TenanciesGet from '../../../../components/tenancies-get'
    import TenanciesList from '../../../../components/tenancies-list'
    import WelcomeModal from '../../landlords/tenancies/index/welcome-modal'

    export default {
        mixins: [
            PaginationMixin
        ],
        components: {
            TenanciesGet,
            TenanciesList,
            WelcomeModal
        },
        computed: {
            include () {
                if (this.$gate.role('landlord')) {
                    return [
                        'property(include:address)',
                        'residencies(paginate:1,5|include:occupant)'
                    ]
                }

                return [
                    'property(include:landlord)'
                ]
            }
        }
    }
</script>
